import { useMemo } from 'react';
import { SearchFilters } from '../components/SearchBar';

export interface SearchableItem {
  id: string;
  title?: string;
  name?: string;
  content?: string;
  status?: string;
  type?: string;
  created_at?: string;
  [key: string]: any;
}

interface UseSearchOptions<T extends SearchableItem> {
  items: T[];
  searchQuery: string;
  filters?: SearchFilters;
  searchFields?: (keyof T)[];
}

export const useSearch = <T extends SearchableItem>({
  items,
  searchQuery,
  filters,
  searchFields = ['title', 'name', 'content'],
}: UseSearchOptions<T>): T[] => {
  return useMemo(() => {
    let filtered = [...items];

    // Apply text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        return searchFields.some((field) => {
          const value = item[field];
          if (typeof value === 'string') {
            return value.toLowerCase().includes(query);
          }
          return false;
        });
      });
    }

    // Apply filters
    if (filters) {
      if (filters.status) {
        filtered = filtered.filter((item) => item.status === filters.status);
      }

      if (filters.type) {
        filtered = filtered.filter((item) => item.type === filters.type);
      }

      if (filters.dateRange) {
        const { start, end } = filters.dateRange;
        filtered = filtered.filter((item) => {
          if (!item.created_at) return false;
          const itemDate = new Date(item.created_at);
          
          if (start && itemDate < start) return false;
          if (end && itemDate > end) return false;
          
          return true;
        });
      }
    }

    return filtered;
  }, [items, searchQuery, filters, searchFields]);
};

